// ===========================================
// Multi-Threshold GWP Enforcement
// ===========================================
// Responsibilities:
// - Enforce GWP threshold/add/remove/quantity rules.
// - Keep existing GWP lines locked at qty 1 in SlideCart and /cart.
// - Give AMP first opportunity to add qualifying gifts, with a JS fallback.
// - Own the ONE SlideCart MutationObserver used by Woobles cart UI modules.
// - Emit a lightweight custom event after relevant SlideCart DOM renders so
//   presentation-only modules can update without creating their own observers.
//
// Modes:
// - active: normal threshold enforcement + AMP-first fallback add.
// - protect_only: never add/remove by threshold; only protect existing gift lines.
// ===========================================

(() => {
  "use strict";

  if (window.__MULTI_THRESHOLD_GWP_INIT__) return;
  window.__MULTI_THRESHOLD_GWP_INIT__ = true;

  // -----------------------------------------
  // Config
  // -----------------------------------------
  const GWP_RULES = [
    // {
    //   name: "55PBN-MAY26 Color-By-Numbers kit",
    //   variantId: "56632402411686",
    //   mode: "active",
    //   thresholdsCents: {
    //     USD: 10000,
    //     CAD: 14000,
    //     GBP: 7500,
    //     AUD: 14500,
    //   },
    // },
    // {
    //   name: "5PEN-MAY26 Pen with Sticky Note",
    //   variantId: "56618200858790",
    //   mode: "active",
    //   thresholdsCents: {
    //     USD: 15000,
    //     CAD: 21000,
    //     GBP: 11500,
    //     AUD: 21500,
    //   },
    // },
  ];

  const DEBUG_GWP = false;

  // A short settling delay for real cart changes. This scheduler is a throttle,
  // not a reset-on-every-mutation debounce, so it cannot be starved by DOM churn.
  const ENFORCE_DELAY_MS = 300;
  const VERIFY_AFTER_CHANGE_MS = 650;

  // AMP gets first opportunity to auto-add before our fallback acts.
  const AMP_ADD_GRACE_MS = 1200;

  // DOM coordination.
  const SLIDECART_ROOT_SELECTOR = "#slidecarthq";
  const SLIDECART_ITEMS_SELECTOR = ".items";
  const SLIDECART_ITEM_SELECTOR = ".item[data-variant-id]";
  const PRESENTATION_ONLY_SELECTOR = "[data-cart-presentation-only='true']";
  const CART_DOM_UPDATED_EVENT = "woobles:cart-dom-updated";

  const LOCKED_ROW_ATTR = "data-threshold-gwp-locked";
  const LOCKED_VARIANT_ATTR = "data-threshold-gwp-variant-id";

  const log = (...args) => {
    if (DEBUG_GWP) {
      console.log("[Threshold GWP]", ...args);
    }
  };

  const warn = (...args) => {
    console.warn("[Threshold GWP]", ...args);
  };

  const shopifyRoot = window.Shopify?.routes?.root || "/";

  const CART_ENDPOINTS = {
    get: `${shopifyRoot}cart.js`,
    add: `${shopifyRoot}cart/add.js`,
    change: `${shopifyRoot}cart/change.js`,
  };

  // -----------------------------------------
  // Validate rules
  // -----------------------------------------
  const activeRules = GWP_RULES.filter((rule) => {
    const variantId = String(rule?.variantId || "").trim();

    if (!/^\d+$/.test(variantId)) {
      warn(`${rule?.name || "Unnamed GWP"}: invalid variant ID`);
      return false;
    }

    return true;
  }).map((rule) => {
    const requestedMode = String(rule?.mode || "active").toLowerCase();
    const mode = requestedMode === "protect_only" ? "protect_only" : "active";

    if (requestedMode !== mode) {
      warn(
        `${rule?.name || "Unnamed GWP"}: unknown mode "${requestedMode}"; defaulting to active`
      );
    }

    return {
      ...rule,
      variantId: String(rule.variantId),
      mode,
    };
  });

  const allGwpVariantIds = new Set(
    activeRules.map((rule) => rule.variantId)
  );

  // Track delayed fallback adds by variant ID.
  const pendingGiftAdds = new Map();

  // -----------------------------------------
  // Selectors
  // -----------------------------------------
  const QUANTITY_INPUT_SELECTOR = [
    '.quantity-selector input[type="text"]',
    '.quantity-selector input[type="number"]',
    "input.cart__qty-input",
    "input.quantity__input",
    'input[name="updates[]"]',
    'input[name^="updates["]',
    'input[id^="Quantity-"]',
    'input[id^="updates_"]',
    'input[id^="updates_large_"]',
    "input[data-quantity-variant-id]",
  ].join(",");

  const QUANTITY_BUTTON_SELECTOR = [
    'button[aria-label*="decrease" i]',
    'button[aria-label*="increase" i]',
    'button[data-testid="decrease-quantity"]',
    'button[data-testid="increase-quantity"]',
    'button[name="minus"]',
    'button[name="plus"]',
    ".quantity__button",
    ".cart__qty-button",
    "[data-quantity-button]",
    '[data-action="increase"]',
    '[data-action="decrease"]',
  ].join(",");

  const QUANTITY_CONTROL_SELECTOR = [
    QUANTITY_INPUT_SELECTOR,
    QUANTITY_BUTTON_SELECTOR,
  ].join(",");

  const CART_ROW_SELECTOR = [
    ".item",
    ".cart-item",
    ".cart__row",
    ".cart__item",
    ".cart-items__row",
    ".line-item",
    "[data-cart-item]",
    "[data-line-item]",
    "tr",
    "li",
  ].join(",");

  const GWP_PRODUCT_LINK_SELECTOR = [
    "a.item-image-anchor",
    ".title a",
  ].join(",");

  // -----------------------------------------
  // Cart API helpers
  // -----------------------------------------
  async function requestJson(url, options = {}) {
    const response = await fetch(url, {
      credentials: "same-origin",
      ...options,
      headers: {
        Accept: "application/json",
        ...(options.headers || {}),
      },
    });

    if (!response.ok) {
      throw new Error(`${url} responded with ${response.status}`);
    }

    return response.json();
  }

  // Serialize our own cart writes. This avoids two fallback/enforcement paths
  // issuing overlapping /cart/add.js or /cart/change.js requests.
  let cartMutationTail = Promise.resolve();

  function enqueueCartMutation(operation) {
    const queued = cartMutationTail.then(operation, operation);

    // Keep the queue usable after an individual mutation fails.
    cartMutationTail = queued.catch(() => undefined);

    return queued;
  }

  async function fetchStableCartJson() {
    await cartMutationTail;
    return requestJson(CART_ENDPOINTS.get);
  }

  function addVariant(variantId, quantity = 1) {
    return enqueueCartMutation(() =>
      requestJson(CART_ENDPOINTS.add, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity,
            },
          ],
        }),
      })
    );
  }

  function setLineQuantity(lineKey, quantity) {
    return enqueueCartMutation(() =>
      requestJson(CART_ENDPOINTS.change, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: lineKey,
          quantity,
        }),
      })
    );
  }

  // -----------------------------------------
  // Threshold helpers
  // -----------------------------------------
  function getCartCurrency(cart) {
    return String(
      cart?.currency ||
        window.Shopify?.currency?.active ||
        "USD"
    ).toUpperCase();
  }

  function getThresholdForCurrency(rule, currency) {
    const threshold = Number(rule?.thresholdsCents?.[currency]);

    if (!Number.isFinite(threshold) || threshold <= 0) {
      return null;
    }

    return Math.round(threshold);
  }

  function getSubtotalExcludingAllGwps(cart) {
    const itemsSubtotal = Number(cart?.items_subtotal_price || 0);

    const gwpLineTotal = (cart?.items || []).reduce((sum, item) => {
      const variantId = String(item.variant_id);

      if (!allGwpVariantIds.has(variantId)) {
        return sum;
      }

      return (
        sum +
        Number(
          item.final_line_price ??
            item.line_price ??
            0
        )
      );
    }, 0);

    return Math.max(0, itemsSubtotal - gwpLineTotal);
  }

  function getGwpLines(cart, variantId) {
    return (cart?.items || []).filter(
      (item) => String(item.variant_id) === String(variantId)
    );
  }

  // -----------------------------------------
  // Delayed AMP-first fallback adds
  // -----------------------------------------
  function cancelPendingGiftAdd(variantId) {
    const variantIdString = String(variantId);
    const timeoutId = pendingGiftAdds.get(variantIdString);

    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
      pendingGiftAdds.delete(variantIdString);
    }
  }

  function scheduleGiftAddAfterAmp(rule) {
    const variantId = String(rule.variantId);

    // Do not queue multiple fallback adds for the same gift.
    if (pendingGiftAdds.has(variantId)) {
      return;
    }

    log(`${rule.name}: waiting for AMP to add gift`);

    const timeoutId = setTimeout(async () => {
      pendingGiftAdds.delete(variantId);

      try {
        // Wait for any cart mutation already initiated by this script, then
        // re-read the cart after AMP has had time to act.
        const freshCart = await fetchStableCartJson();
        const freshCurrency = getCartCurrency(freshCart);
        const freshThreshold = getThresholdForCurrency(rule, freshCurrency);

        if (freshThreshold === null) {
          return;
        }

        const freshSubtotal = getSubtotalExcludingAllGwps(freshCart);
        const stillQualifies = freshSubtotal >= freshThreshold;
        const freshGwpLines = getGwpLines(freshCart, variantId);

        // AMP successfully added it.
        if (freshGwpLines.length) {
          log(`${rule.name}: AMP added gift`);
          scheduleUiRefresh("amp_added_gift");
          scheduleGwpEnforcement(0, "amp_added_gift");
          return;
        }

        // Cart changed during the grace period.
        if (!stillQualifies) {
          log(`${rule.name}: no longer qualifies; skipping fallback add`);
          return;
        }

        // AMP did not add it, so add/re-add it ourselves.
        log(`${rule.name}: still missing after AMP grace period; adding gift`);
        await addVariant(variantId, 1);

        window.SLIDECART_UPDATE?.();
        scheduleEnsureCartDomObserver();
        scheduleUiRefresh("fallback_gift_added");
        scheduleGwpEnforcement(
          VERIFY_AFTER_CHANGE_MS,
          "fallback_gift_added"
        );
      } catch (error) {
        console.error(
          `[Threshold GWP] Failed delayed add for ${rule.name}`,
          error
        );
      }
    }, AMP_ADD_GRACE_MS);

    pendingGiftAdds.set(variantId, timeoutId);
  }

  // -----------------------------------------
  // UI locking helpers
  // -----------------------------------------
  function disableQuantityButton(button) {
    if (!button) return;

    button.disabled = true;
    button.setAttribute("aria-disabled", "true");
    button.classList.add("gwp-locked-qty");
  }

  function lockQuantityInput(input) {
    if (!input) return;

    input.readOnly = true;
    input.setAttribute("readonly", "readonly");
    input.setAttribute("aria-disabled", "true");
    input.setAttribute("min", "1");
    input.setAttribute("max", "1");
    input.value = "1";
    input.classList.add("gwp-locked-qty");
  }

  function getRowsForVariant(variantId) {
    const variantIdString = String(variantId);
    const rows = new Set();

    const directSelectors = [
      `[data-variant-id="${variantIdString}"]`,
      `[data-product-variant-id="${variantIdString}"]`,
      `[data-cart-item-variant-id="${variantIdString}"]`,
      `[data-quantity-variant-id="${variantIdString}"]`,
      `input[name="updates[${variantIdString}]"]`,
      `input[id="updates_${variantIdString}"]`,
      `input[id="updates_large_${variantIdString}"]`,
      `input[id*="${variantIdString}"]`,
    ];

    document
      .querySelectorAll(directSelectors.join(","))
      .forEach((node) => {
        rows.add(node.closest(CART_ROW_SELECTOR) || node);
      });

    // Fallback for themes/apps that encode the variant ID only in quantity
    // control attributes rather than on the row itself.
    document
      .querySelectorAll(QUANTITY_INPUT_SELECTOR)
      .forEach((input) => {
        const identifyingText = [
          input.id || "",
          input.name || "",
          input.getAttribute("data-quantity-variant-id") || "",
          input.closest("[data-variant-id]")?.getAttribute("data-variant-id") || "",
          input
            .closest("[data-product-variant-id]")
            ?.getAttribute("data-product-variant-id") || "",
          input
            .closest("[data-cart-item-variant-id]")
            ?.getAttribute("data-cart-item-variant-id") || "",
        ].join(" ");

        if (identifyingText.includes(variantIdString)) {
          rows.add(input.closest(CART_ROW_SELECTOR) || input);
        }
      });

    return [...rows];
  }

  function disableGwpProductLinks(row) {
    if (!row || !(row instanceof Element)) return;

    row.querySelectorAll(GWP_PRODUCT_LINK_SELECTOR).forEach((link) => {
      if (link.hasAttribute("href") && !link.dataset.gwpOriginalHref) {
        link.dataset.gwpOriginalHref = link.getAttribute("href");
      }

      link.removeAttribute("href");
      link.setAttribute("aria-disabled", "true");
      link.setAttribute("tabindex", "-1");
      link.classList.add("gwp-product-link-disabled");
      link.style.pointerEvents = "none";
      link.style.cursor = "default";
    });
  }

  function lockGwpQuantityControls(variantId) {
    getRowsForVariant(variantId).forEach((row) => {
      if (!(row instanceof Element)) return;

      row.setAttribute(LOCKED_ROW_ATTR, "true");
      row.setAttribute(LOCKED_VARIANT_ATTR, String(variantId));
      row.classList.add("threshold-gwp-locked");

      disableGwpProductLinks(row);

      row
        .querySelectorAll(QUANTITY_BUTTON_SELECTOR)
        .forEach(disableQuantityButton);

      row
        .querySelectorAll(QUANTITY_INPUT_SELECTOR)
        .forEach(lockQuantityInput);

      if (row.matches(QUANTITY_BUTTON_SELECTOR)) {
        disableQuantityButton(row);
      }

      if (row.matches(QUANTITY_INPUT_SELECTOR)) {
        lockQuantityInput(row);
      }
    });
  }

  function lockAllExistingGwpRows() {
    for (const rule of activeRules) {
      lockGwpQuantityControls(rule.variantId);
    }
  }

  // Return a protected variant ID when the clicked/edited control can be
  // identified even before a freshly-rendered row has received lock attributes.
  function getProtectedVariantIdForElement(element) {
    if (!(element instanceof Element)) return null;

    const lockedRow = element.closest(`[${LOCKED_ROW_ATTR}="true"]`);
    if (lockedRow) {
      const lockedVariant = lockedRow.getAttribute(LOCKED_VARIANT_ATTR);
      if (lockedVariant && allGwpVariantIds.has(lockedVariant)) {
        return lockedVariant;
      }
    }

    const identifyingText = [
      element.id || "",
      element.getAttribute("name") || "",
      element.getAttribute("data-variant-id") || "",
      element.getAttribute("data-product-variant-id") || "",
      element.getAttribute("data-cart-item-variant-id") || "",
      element.getAttribute("data-quantity-variant-id") || "",
      element.closest("[data-variant-id]")?.getAttribute("data-variant-id") || "",
      element
        .closest("[data-product-variant-id]")
        ?.getAttribute("data-product-variant-id") || "",
      element
        .closest("[data-cart-item-variant-id]")
        ?.getAttribute("data-cart-item-variant-id") || "",
    ].join(" ");

    for (const variantId of allGwpVariantIds) {
      if (identifyingText.includes(variantId)) {
        return variantId;
      }
    }

    return null;
  }

  // -----------------------------------------
  // Hard interaction guards
  // -----------------------------------------
  function blockLockedGwpProductLink(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const link = target.closest(GWP_PRODUCT_LINK_SELECTOR);
    if (!link) return;

    const protectedVariantId = getProtectedVariantIdForElement(link);
    if (!protectedVariantId) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();
  }

  document.addEventListener("click", blockLockedGwpProductLink, true);

  function blockLockedQuantityControl(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const control = target.closest(QUANTITY_CONTROL_SELECTOR);
    if (!control) return;

    const protectedVariantId = getProtectedVariantIdForElement(control);
    if (!protectedVariantId) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();

    if (control.matches(QUANTITY_INPUT_SELECTOR)) {
      control.value = "1";
    }

    // Reapply visual locks immediately. No cart fetch is needed for this.
    lockGwpQuantityControls(protectedVariantId);

    // A malicious/manual mutation may still have reached the cart API outside
    // this handler, so keep authoritative enforcement scheduled as a backstop.
    scheduleGwpEnforcement(0, "blocked_quantity_change");
  }

  [
    "pointerdown",
    "mousedown",
    "touchstart",
    "click",
    "keydown",
    "input",
    "change",
  ].forEach((eventName) => {
    document.addEventListener(
      eventName,
      blockLockedQuantityControl,
      true
    );
  });

  // Normal cart interactions should trigger authoritative reconciliation even
  // if AMP/the theme does not emit one of the custom cart events below.
  function handleGeneralCartInteraction(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // Ignore interactions with a protected GWP itself; those are handled above.
    if (getProtectedVariantIdForElement(target)) return;

    const slideCartControl = target.closest(
      `${SLIDECART_ROOT_SELECTOR} ${QUANTITY_CONTROL_SELECTOR}`
    );

    const cartPageControl = target.closest(
      `form[action*="/cart"] ${QUANTITY_CONTROL_SELECTOR}`
    );

    if (!slideCartControl && !cartPageControl) return;

    scheduleGwpEnforcement(ENFORCE_DELAY_MS, "cart_quantity_interaction");
  }

  ["click", "input", "change"].forEach((eventName) => {
    document.addEventListener(eventName, handleGeneralCartInteraction, false);
  });

  // -----------------------------------------
  // UI refresh + shared SlideCart DOM signal
  // -----------------------------------------
  let uiRefreshFrame = 0;
  let pendingUiReason = "unknown";

  function emitCartDomUpdated(reason) {
    document.dispatchEvent(
      new CustomEvent(CART_DOM_UPDATED_EVENT, {
        detail: { reason },
      })
    );
  }

  function scheduleUiRefresh(reason = "cart_dom") {
    pendingUiReason = reason;

    if (uiRefreshFrame) return;

    uiRefreshFrame = window.requestAnimationFrame(() => {
      uiRefreshFrame = 0;
      const reasonForPass = pendingUiReason;
      pendingUiReason = "unknown";

      lockAllExistingGwpRows();
      emitCartDomUpdated(reasonForPass);
    });
  }

  // -----------------------------------------
  // ONE shared SlideCart MutationObserver
  // -----------------------------------------
  // Important performance property: this observer is NEVER attached to
  // document.body. It observes only #slidecarthq when that root exists. If AMP
  // mounts/replaces the root lazily, lightweight cart events/clicks re-check for
  // it without monitoring unrelated page mutations.
  const cartDomObserver = new MutationObserver(handleCartDomMutations);
  let observedSlideCartRoot = null;
  let ensureObserverFrame = 0;

  function isPresentationOnlyNode(node) {
    return (
      node instanceof Element &&
      (node.matches(PRESENTATION_ONLY_SELECTOR) ||
        Boolean(node.closest(PRESENTATION_ONLY_SELECTOR)))
    );
  }

  function mutationTouchesRealCartDom(mutation) {
    const target = mutation.target;

    // Presentation-only modules must never trigger cart reconciliation.
    if (
      target instanceof Element &&
      target.closest(PRESENTATION_ONLY_SELECTOR)
    ) {
      return false;
    }

    const changedNodes = [
      ...mutation.addedNodes,
      ...mutation.removedNodes,
    ];

    const meaningfulNodes = changedNodes.filter((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        return !(
          node.parentElement &&
          node.parentElement.closest(PRESENTATION_ONLY_SELECTOR)
        );
      }

      return !isPresentationOnlyNode(node);
    });

    // A mutation consisting entirely of our own visual rows is irrelevant.
    if (changedNodes.length && !meaningfulNodes.length) {
      return false;
    }

    // Real cart line rows were added/removed/replaced.
    if (
      meaningfulNodes.some(
        (node) =>
          node instanceof Element &&
          (node.matches(SLIDECART_ITEM_SELECTOR) ||
            Boolean(node.querySelector(SLIDECART_ITEM_SELECTOR)))
      )
    ) {
      return true;
    }

    // A child inside a real cart line changed (AMP frequently replaces nested
    // quantity/price markup without replacing the outer .item node).
    if (
      target instanceof Element &&
      target.closest(SLIDECART_ITEM_SELECTOR)
    ) {
      return true;
    }

    // The .items container itself changed in a non-presentation-only way.
    if (
      target instanceof Element &&
      (target.matches(SLIDECART_ITEMS_SELECTOR) ||
        target.closest(SLIDECART_ITEMS_SELECTOR)) &&
      meaningfulNodes.length
    ) {
      return true;
    }

    return false;
  }

  function ensureCartDomObserver() {
    const currentRoot = document.querySelector(SLIDECART_ROOT_SELECTOR);

    if (!currentRoot) {
      if (observedSlideCartRoot) {
        cartDomObserver.disconnect();
        observedSlideCartRoot = null;
      }
      return false;
    }

    if (
      observedSlideCartRoot === currentRoot &&
      currentRoot.isConnected
    ) {
      return true;
    }

    cartDomObserver.disconnect();
    observedSlideCartRoot = currentRoot;

    cartDomObserver.observe(currentRoot, {
      childList: true,
      subtree: true,
    });

    scheduleUiRefresh("slidecart_mounted");
    return true;
  }

  function scheduleEnsureCartDomObserver() {
    if (ensureObserverFrame) return;

    ensureObserverFrame = window.requestAnimationFrame(() => {
      ensureObserverFrame = 0;
      ensureCartDomObserver();
    });
  }

  function handleCartDomMutations(mutations) {
    if (
      observedSlideCartRoot &&
      !observedSlideCartRoot.isConnected
    ) {
      scheduleEnsureCartDomObserver();
      return;
    }

    if (!mutations.some(mutationTouchesRealCartDom)) {
      return;
    }

    // DOM work is cheap and immediate: relock current GWP rows and notify other
    // presentation modules. This callback itself performs ZERO network work.
    scheduleUiRefresh("slidecart_dom_change");

    // Authoritative cart reconciliation is coalesced and non-starvable.
    scheduleGwpEnforcement(ENFORCE_DELAY_MS, "slidecart_dom_change");
  }

  // AMP commonly has #slidecarthq mounted from page load. If it mounts lazily,
  // a user interaction is a much cheaper discovery signal than observing all of
  // document.body indefinitely. Once mounted, this listener becomes effectively
  // a no-op because ensureCartDomObserver exits immediately for the same root.
  document.addEventListener(
    "click",
    () => {
      if (
        !observedSlideCartRoot ||
        !observedSlideCartRoot.isConnected
      ) {
        scheduleEnsureCartDomObserver();
      }
    },
    true
  );

  // -----------------------------------------
  // Core authoritative enforcement
  // -----------------------------------------
  let enforcing = false;
  let enforcementTimer = null;
  let enforcementRequestedWhileRunning = false;
  let scheduledReason = "unknown";

  function scheduleGwpEnforcement(
    delay = ENFORCE_DELAY_MS,
    reason = "scheduled"
  ) {
    // If a pass is already running, request exactly one follow-up pass.
    if (enforcing) {
      enforcementRequestedWhileRunning = true;
      return;
    }

    // Immediate requests supersede a later pending request.
    if (delay === 0) {
      if (enforcementTimer !== null) {
        clearTimeout(enforcementTimer);
      }

      scheduledReason = reason;
      enforcementTimer = setTimeout(runScheduledEnforcement, 0);
      return;
    }

    // NON-STARVABLE THROTTLE: once scheduled, repeated mutations do not keep
    // pushing the pass farther into the future.
    if (enforcementTimer !== null) {
      return;
    }

    scheduledReason = reason;
    enforcementTimer = setTimeout(runScheduledEnforcement, delay);
  }

  function runScheduledEnforcement() {
    enforcementTimer = null;
    const reason = scheduledReason;
    scheduledReason = "unknown";

    log("Scheduled enforcement", { reason });
    enforceGwpRules();
  }

  async function enforceGwpRules() {
    if (enforcing) {
      enforcementRequestedWhileRunning = true;
      return;
    }

    if (!activeRules.length) return;

    enforcing = true;
    enforcementRequestedWhileRunning = false;

    try {
      const cart = await fetchStableCartJson();
      const currency = getCartCurrency(cart);
      const qualifyingSubtotal = getSubtotalExcludingAllGwps(cart);

      let cartChanged = false;

      for (const rule of activeRules) {
        const gwpLines = getGwpLines(cart, rule.variantId);

        // -------------------------------------
        // PROTECT-ONLY MODE
        // -------------------------------------
        if (rule.mode === "protect_only") {
          cancelPendingGiftAdd(rule.variantId);

          log(`${rule.name}: protect-only check`, {
            lineCount: gwpLines.length,
          });

          // No existing gift = do absolutely nothing.
          if (!gwpLines.length) {
            continue;
          }

          const [primaryGiftLine, ...duplicateGiftLines] = gwpLines;

          for (const duplicateLine of duplicateGiftLines) {
            await setLineQuantity(duplicateLine.key, 0);
            cartChanged = true;
          }

          if (primaryGiftLine.quantity !== 1) {
            await setLineQuantity(primaryGiftLine.key, 1);
            cartChanged = true;
          }

          lockGwpQuantityControls(rule.variantId);
          continue;
        }

        // -------------------------------------
        // ACTIVE MODE
        // -------------------------------------
        const threshold = getThresholdForCurrency(rule, currency);

        if (threshold === null) {
          cancelPendingGiftAdd(rule.variantId);
          warn(
            `${rule.name}: no valid ${currency} threshold configured; skipping`
          );
          continue;
        }

        const meetsThreshold = qualifyingSubtotal >= threshold;

        log(`${rule.name}: threshold check`, {
          currency,
          qualifyingSubtotal,
          threshold,
          meetsThreshold,
          lineCount: gwpLines.length,
        });

        // Qualifies but gift is missing: AMP gets first chance.
        if (meetsThreshold && !gwpLines.length) {
          scheduleGiftAddAfterAmp(rule);
          continue;
        }

        // Missing and does not qualify.
        if (!meetsThreshold && !gwpLines.length) {
          cancelPendingGiftAdd(rule.variantId);
          continue;
        }

        // Does not qualify but gift is present: remove every line.
        if (!meetsThreshold && gwpLines.length) {
          cancelPendingGiftAdd(rule.variantId);

          for (const item of gwpLines) {
            await setLineQuantity(item.key, 0);
          }

          cartChanged = true;
          continue;
        }

        // Gift is present, so cancel any delayed fallback add.
        cancelPendingGiftAdd(rule.variantId);

        // Keep first line, remove duplicate lines.
        const [primaryGiftLine, ...duplicateGiftLines] = gwpLines;

        for (const duplicateLine of duplicateGiftLines) {
          await setLineQuantity(duplicateLine.key, 0);
          cartChanged = true;
        }

        // Normalize merged/raced quantity back to one.
        if (primaryGiftLine.quantity !== 1) {
          await setLineQuantity(primaryGiftLine.key, 1);
          cartChanged = true;
        }

        lockGwpQuantityControls(rule.variantId);
      }

      // Always do a cheap UI pass after authoritative reconciliation. If AMP has
      // just re-rendered, this immediately locks the current DOM nodes.
      scheduleUiRefresh("enforcement_complete");

      if (cartChanged) {
        window.SLIDECART_UPDATE?.();
        scheduleEnsureCartDomObserver();

        scheduleGwpEnforcement(
          VERIFY_AFTER_CHANGE_MS,
          "verify_after_change"
        );
      }
    } catch (error) {
      console.error("[Threshold GWP] Error enforcing GWP rules", error);
    } finally {
      enforcing = false;

      if (enforcementRequestedWhileRunning) {
        enforcementRequestedWhileRunning = false;
        scheduleGwpEnforcement(
          VERIFY_AFTER_CHANGE_MS,
          "followup_after_running"
        );
      }
    }
  }

  // -----------------------------------------
  // Cart lifecycle events
  // -----------------------------------------
  [
    "cart:added",
    "cart:updated",
    "cart:changed",
    "cart:refresh",
  ].forEach((eventName) => {
    document.addEventListener(eventName, () => {
      scheduleEnsureCartDomObserver();
      scheduleUiRefresh(eventName);
      scheduleGwpEnforcement(ENFORCE_DELAY_MS, eventName);
    });
  });

  // -----------------------------------------
  // Initialization
  // -----------------------------------------
  function init() {
    ensureCartDomObserver();
    scheduleUiRefresh("init");
    scheduleGwpEnforcement(0, "init");
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();